import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Inject,
  ViewChild,
  HostListener,
  ElementRef,
  NgZone
} from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { HeaderComponent } from '../../header/header.component';
import { NavigationComponent } from '../../navigation/navigation.component';
import { FaceScanComponent } from '../../face-scan/face-scan.component';
import { AuthService, CameraCleanupService } from '../../../services';
import { CONSTANTS_TOKEN } from '../../../config';
import { PortalType } from '../../../enums';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'educare-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [CommonModule, TranslateModule, RouterModule, HeaderComponent, NavigationComponent, FaceScanComponent]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(FaceScanComponent) faceScanComponent?: FaceScanComponent;

  sidebarOpen = false;
  isFaceVerification = false;
  showFaceScanner = false;
  private destroy$ = new Subject<void>();
  private userSubscriptionInitialized = false;
  private iframeCheckInterval: any;
  private isLoggingOut = false;
  private isReattaching = false;
  private domObserver?: MutationObserver;

  constructor(
    private router: Router,
    private authService: AuthService,
    private cameraCleanup: CameraCleanupService,
    private elementRef: ElementRef,
    private ngZone: NgZone,
    @Inject(CONSTANTS_TOKEN) private constants: any
  ) {}

  ngOnInit(): void {
    if (this.constants.app.portalType === PortalType.User) {
      this.isFaceVerification = this.router.url === '/face-verification';
      this.initializeUserSubscription();
      this.checkFaceScannerState();
      this.iframeCheckInterval = setInterval(() => {
        this.checkFaceScannerState();
      }, 500);
      this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          this.isFaceVerification = this.router.url === '/face-verification';
          if (this.isFaceVerification) {
            this.closeFaceScan();
          } else {
            this.checkFaceScannerState();
          }
        }
      });
      this.authService.beforeLogout$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.isLoggingOut = true;
        if (this.iframeCheckInterval) {
          clearInterval(this.iframeCheckInterval);
          this.iframeCheckInterval = null;
        }
        this.closeFaceScan();
        if (this.faceScanComponent) {
          this.faceScanComponent.ngOnDestroy();
        }
      });
    } else {
      this.isFaceVerification = false;
    }
  }

  private initializeUserSubscription() {
    if (!this.userSubscriptionInitialized) {
      this.userSubscriptionInitialized = true;
      this.authService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (!this.isFaceVerification) {
          this.checkFaceScannerState();
        }
      });
      const userInfo = this.authService.getUserInfo();
      const fr = userInfo?.faceRegistration;
      if (fr?.isFaceRecognitionEnabled === true && fr?.isRegistered && !this.isFaceVerification) {
        this.checkFaceScannerState();
      }
    }
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  checkFaceScannerState() {
    if (this.isLoggingOut) {
      return;
    }

    if (!this.isFaceVerification) {
      const userInfo = this.authService.getUserInfo();
      if (!userInfo) {
        this.isLoggingOut = true;
        if (this.iframeCheckInterval) {
          clearInterval(this.iframeCheckInterval);
          this.iframeCheckInterval = null;
        }
        this.closeFaceScan();
        return;
      }
      const fr = userInfo?.faceRegistration;
      const isFaceRecognitionEnabled = fr?.isFaceRecognitionEnabled === true;
      const isFaceRegistered = fr?.isRegistered || false;
      if (isFaceRecognitionEnabled && isFaceRegistered) {
        if (!this.showFaceScanner) {
          this.openFaceScan();
        }
      } else {
        if (this.showFaceScanner) {
          this.closeFaceScan();
        }
      }
    } else {
      if (this.showFaceScanner) {
        this.closeFaceScan();
      }
    }
  }

  ngAfterViewInit(): void {
    this.observeFaceScanDom();
  }

  private observeFaceScanDom() {
    this.ngZone.runOutsideAngular(() => {
      this.domObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          const removedFaceScan = Array.from(mutation.removedNodes).some(
            (node) => node instanceof HTMLElement && node.tagName === 'EDUCARE-FACE-SCAN'
          );
          if (removedFaceScan && this.showFaceScanner && !this.isLoggingOut && !this.isReattaching) {
            this.isReattaching = true;
            this.ngZone.run(() => (this.showFaceScanner = false));
            setTimeout(() => {
              if (!this.isLoggingOut) {
                this.ngZone.run(() => (this.showFaceScanner = true));
              }
              this.isReattaching = false;
            }, 1000);
          }
        }
      });
      this.domObserver.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
    });
  }

  checkAxonifyIframeState() {
    this.checkFaceScannerState();
  }

  validateFaceScan() {
    this.checkFaceScannerState();
  }

  openFaceScan() {
    if (this.isLoggingOut) {
      return;
    }
    this.showFaceScanner = true;
  }

  closeFaceScan() {
    this.showFaceScanner = false;
  }

  onFaceScanCloseRequested() {
    this.closeFaceScan();
  }

  onScreenLocked() {
    // If user is in learning-hub (Axonify iframe), redirect to dashboard
    // This prevents the user from being stuck in the iframe when screen is locked
    if (this.router.url.includes('learning-hub')) {
      this.router.navigate(['/dashboard']);
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.isLoggingOut = true;
    if (this.iframeCheckInterval) {
      clearInterval(this.iframeCheckInterval);
      this.iframeCheckInterval = null;
    }
    this.cameraCleanup.stopAllMediaStreams();
  }

  ngOnDestroy(): void {
    this.isLoggingOut = true;
    this.domObserver?.disconnect();
    if (this.iframeCheckInterval) {
      clearInterval(this.iframeCheckInterval);
      this.iframeCheckInterval = null;
    }
    this.closeFaceScan();
    if (this.faceScanComponent) {
      this.faceScanComponent.ngOnDestroy();
    }
    this.cameraCleanup.stopAllMediaStreams();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
